// Portal KPI store — müşteri aktörü için ince Zustand store'u (M16 T19). Danışman `kpi-store`'una
// DOKUNULMAZ (byte-eşit kalır); portal API tabanı (`api/portal-kpi.ts`) ayrıdır: müşteri listesi
// yok, set listesi `GET /portal/kpi/sets`'ten (erişim yoksa boş), hücreler `editable` bayrağıyla.
// `saveCell` optimistik yazar; 409 KPI_STALE_WRITE'ta rollback + `stale` (yalnız "sunucudaki
// değeri göster" — üzerine yazma YOK, DR8). Set seçimi kullanıcı bazında localStorage'da
// (kpi-ui `readKpiSelection` — clientId null). ClientShell `loadSets` ile sekme görünürlüğünü
// (DR1) ve ClientHome landing kararını (DR2) bu store'dan okur.
// Çok işletmeli müşteri (P1-1): `entities` sunucunun tam listesi, `entityId` seçili yaprak ya da
// PORTAL_CONSOLIDATED; grid seçili işletmeye daraltılır (`scopeGridToEntity`) ve sonraki yenilemeler
// `entityIds=` ile dar çekilir. Seçim kullanıcı+set bazında localStorage'da.
// K8 (2026-10-23): çok firmalı müşteride set listesi portal-store'un aktif firmasına süzülür
// (`loadSets(userId, {clientId})`); firma değişince portal-store bu store'u `reset()` eder.
// D20/DR9: yazım gösterge birimini beyan eder; 409 KPI_UNIT_CHANGED → geri alma + `unitChanged`
// hücre durumu (editörde amber satır) + yerel birim güncellemesi (yeniden yükleme yok).
// Kırılım (spec §6.5 DR4, kpi-store kalıbı): `pendingCells` (hücre anahtarı → bekleyen yazım) grid
// yenilenene kadar tutulur — toplam hücresi `selectPortalTotalRecomputing` ile soluk + aria-busy.

import { create } from "zustand";
import type {
  ClientEntity,
  KpiResolvedCell,
  KpiResolvedGrid,
  KpiValueStatus,
  SetFillStatus,
} from "@mahir/shared";
import type { KpiValueDto } from "../api/kpi";
import {
  getPortalKpiResolved,
  listPortalKpiSets,
  putPortalValueRaw,
  type PortalAccessSummary,
  type PortalSetSummary,
} from "../api/portal-kpi";
import { isWriteBlockedIndicator, totalRecomputing } from "../lib/kpi-grid-breakdown";
import type { GridColumn } from "../lib/kpi-grid-nav";
import { defaultKpiYears, KPI_COPY, readKpiSelection, writeKpiSelection } from "../lib/kpi-ui";
import {
  buildPortalUnitChanged,
  patchIndicatorUnit,
  type PortalUnitChanged,
} from "../lib/kpi-unit-changed";
import {
  decidePortalEntity,
  PORTAL_CONSOLIDATED,
  portalErrorMessage,
  scopeGridToEntity,
} from "../lib/portal-kpi-ui";

export type PortalSetsStatus = "idle" | "loading" | "ready" | "error";

export interface PortalSaveCellArgs {
  metricId: string;
  entityId: string;
  year: number;
  status: KpiValueStatus;
  numericValue?: number;
  textValue?: string;
  footnote?: string;
  /** açıklama isteğine yanıt (P3-17) — sunucu `clientNote` kabul eder */
  clientNote?: string;
}

export interface PortalStale {
  key: string;
  current: KpiValueDto | null;
  message: string;
}

export interface PortalKpiState {
  userId: string | null;
  setsStatus: PortalSetsStatus;
  sets: PortalSetSummary[];
  /** firma süzgeci UYGULANMAMIŞ erişilebilir setler — derin bağlantı başka firmanın setini çözer (§12/9) */
  allSets: PortalSetSummary[];
  setId: string | null;
  years: number[];
  /** sunucunun tam işletme listesi (seçici + içe aktarma bağlamı) */
  entities: ClientEntity[];
  /** seçili yaprak işletme id'si ya da PORTAL_CONSOLIDATED (salt okunur); tek işletmede yaprak id */
  entityId: string | null;
  /** birden çok aktif yaprak → seçici görünür */
  entityChoice: boolean;
  grid: KpiResolvedGrid | null;
  /** sunucunun daraltılmamış yanıtı — seçili işletme görünümünde toplam sütun grubu için (§12/1) */
  fullGrid: KpiResolvedGrid | null;
  fill: SetFillStatus | null;
  access: PortalAccessSummary | null;
  cycle: string[] | null;
  gridLoading: boolean;
  error: string | null;
  pendingSaves: number;
  /** DR4 — bekleyen yazımlar hücre bazında (`metricId|entityId|year` → adet), grid yenilenene kadar */
  pendingCells: Record<string, number>;
  saveErrors: Record<string, string>;
  lastSavedAt: string | null;
  stale: PortalStale | null;
  /** DR9 — hücre anahtarı → birim değişti (409 KPI_UNIT_CHANGED); editörde amber satır, grid'de nokta */
  unitChanged: Record<string, PortalUnitChanged>;

  /** set listesi (idempotent; `force` ile yeniden). Hata → `setsStatus:"error"` (fail-open). */
  loadSets: (userId: string, opts?: { force?: boolean; clientId?: string | null }) => Promise<void>;
  /** K8 — set listesi bu firmaya süzülür (null → süzme yok; portal-store aktif firması). */
  clientFilter: string | null;
  selectSet: (setId: string | null) => Promise<void>;
  setYears: (years: number[]) => Promise<void>;
  selectEntity: (entityId: string) => Promise<void>;
  refreshGrid: () => Promise<void>;
  saveCell: (args: PortalSaveCellArgs) => Promise<boolean>;
  /** stale modalı: sunucudaki değeri göster → grid tazelenir */
  acknowledgeStale: () => Promise<void>;
  reset: () => void;
}

function cellKey(metricId: string, entityId: string, year: number): string {
  return `${metricId}|${entityId}|${year}`;
}

/** Bekleyen hücre sayacı: +1 / −1 (0'a inen anahtar silinir) — kpi-store ile aynı kural. */
function bumpPending(
  pending: Readonly<Record<string, number>>,
  key: string,
  delta: 1 | -1,
): Record<string, number> {
  const next = { ...pending };
  const n = (next[key] ?? 0) + delta;
  if (n > 0) next[key] = n;
  else delete next[key];
  return next;
}

/**
 * DR4 bekleme seçicisi: görünür toplam satırının (indicator.id) bu kolondaki hücresi, hesap grubu
 * üyesinin kaydı sürerken (grid yenilemesi gelene kadar) meşgul. Toplam gizliyse (K3) satır yok.
 */
export function selectPortalTotalRecomputing(
  totalId: string,
  col: Pick<GridColumn, "entityId" | "year" | "aggregate">,
): (s: Pick<PortalKpiState, "grid" | "pendingCells">) => boolean {
  return (s) => {
    const total = s.grid?.indicators.find((i) => i.id === totalId);
    return !!total && !!s.grid && totalRecomputing(s.pendingCells, total, s.grid.indicators, col);
  };
}

function entityKey(userId: string): string {
  return `mahir.kpi.portal.entity.${userId}`;
}
function readEntitySelection(userId: string | null, setId: string): string | null {
  if (!userId || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(entityKey(userId));
    const map = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    const v = map[setId];
    return typeof v === "string" ? v : null;
  } catch {
    return null;
  }
}
function writeEntitySelection(userId: string | null, setId: string, entityId: string): void {
  if (!userId || typeof window === "undefined") return;
  try {
    const raw = window.localStorage.getItem(entityKey(userId));
    const map = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    map[setId] = entityId;
    window.localStorage.setItem(entityKey(userId), JSON.stringify(map));
  } catch {
    // quota / private mode — seçim hatırlanmaz
  }
}

function patchGridCell(
  grid: KpiResolvedGrid,
  metricId: string,
  entityId: string,
  year: number,
  patch: (prev: KpiResolvedCell | undefined) => KpiResolvedCell,
): KpiResolvedGrid {
  return {
    ...grid,
    indicators: grid.indicators.map((ind) => {
      if (ind.metricId !== metricId) return ind;
      const byYear = { ...(ind.cells[entityId] ?? {}) };
      byYear[year] = patch(byYear[year]);
      return { ...ind, cells: { ...ind.cells, [entityId]: byYear } };
    }),
  };
}

export const usePortalKpiStore = create<PortalKpiState>()((set, get) => ({
  userId: null,
  setsStatus: "idle",
  sets: [],
  allSets: [],
  clientFilter: null,
  setId: null,
  years: defaultKpiYears(),
  entities: [],
  entityId: null,
  entityChoice: false,
  grid: null,
  fullGrid: null,
  fill: null,
  access: null,
  cycle: null,
  gridLoading: false,
  error: null,
  pendingSaves: 0,
  pendingCells: {},
  saveErrors: {},
  lastSavedAt: null,
  stale: null,
  unitChanged: {},

  async loadSets(userId, opts = {}) {
    const st = get();
    const clientFilter = opts.clientId ?? null;
    if (
      !opts.force &&
      st.userId === userId &&
      st.clientFilter === clientFilter &&
      (st.setsStatus === "ready" || st.setsStatus === "loading")
    )
      return;
    set({ userId, clientFilter, setsStatus: "loading" });
    try {
      const all = await listPortalKpiSets();
      if (get().clientFilter !== clientFilter) return; // bu arada firma değişti — bayat yanıt
      const sets = clientFilter ? all.filter((x) => x.clientId === clientFilter) : all;
      const stored = readKpiSelection(userId);
      const wanted =
        st.setId && sets.some((s) => s.id === st.setId)
          ? st.setId
          : stored.setId && sets.some((s) => s.id === stored.setId)
            ? stored.setId
            : (sets[0]?.id ?? null);
      set({ sets, allSets: all, setsStatus: "ready" });
      if (wanted !== get().setId || (wanted && !get().grid)) await get().selectSet(wanted);
    } catch {
      set({ sets: [], allSets: [], setsStatus: "error" });
    }
  },

  async selectSet(setId) {
    set({
      setId,
      grid: null,
      fullGrid: null,
      fill: null,
      access: null,
      cycle: null,
      error: null,
      stale: null,
      unitChanged: {},
      entities: [],
      entityId: null,
      entityChoice: false,
    });
    const { userId } = get();
    if (userId) writeKpiSelection(userId, { clientId: null, setId });
    if (setId) await get().refreshGrid();
  },

  async selectEntity(entityId) {
    const { setId, userId } = get();
    if (!setId || entityId === get().entityId) return;
    set({ entityId, stale: null, saveErrors: {} });
    writeEntitySelection(userId, setId, entityId);
    await get().refreshGrid();
  },

  async setYears(years) {
    set({ years: [...years].sort((a, b) => a - b) });
    if (get().setId) await get().refreshGrid();
  },

  async refreshGrid() {
    const { setId, years, userId } = get();
    if (!setId) return;
    set({ gridLoading: true, error: null });
    try {
      let { entityId } = get();
      const scoped = entityId !== null && entityId !== PORTAL_CONSOLIDATED;
      const res = await getPortalKpiResolved(setId, {
        years,
        ...(scoped ? { entityIds: [entityId!] } : {}),
      });
      if (get().setId !== setId) return; // bu arada set değişti — bayat yanıt atılır
      let grid = res.grid;
      if (!scoped) {
        // tam liste: işletme kararı (ilk yükleme ya da konsolide görünüm)
        const choice = decidePortalEntity(res.grid.entities, readEntitySelection(userId, setId));
        entityId = entityId ?? choice.selected;
        set({ entities: res.grid.entities, entityChoice: choice.show, entityId });
        if (entityId && entityId !== PORTAL_CONSOLIDATED && choice.show)
          grid = scopeGridToEntity(res.grid, entityId);
      } else if (get().entities.length === 0) {
        set({ entities: res.grid.entities });
      }
      if (scoped && entityId) grid = scopeGridToEntity(res.grid, entityId);
      set({ grid, fullGrid: res.grid, fill: res.fill, access: res.access, cycle: res.cycle });
    } catch (err) {
      // güvenlik/UX taraması 2026-10-08 (RK36): satır içi metin yerine tek kaynak
      set({ error: err instanceof Error ? err.message : KPI_COPY.portal.loadFailed });
    } finally {
      set({ gridLoading: false });
    }
  },

  async saveCell(args) {
    const { grid, setId } = get();
    if (!grid || !setId) return false;
    const ind = grid.indicators.find((i) => i.metricId === args.metricId);
    if (!ind || isWriteBlockedIndicator(ind, grid.indicators)) return false; // + kırılım toplamı/grup
    const key = cellKey(args.metricId, args.entityId, args.year);
    const prev = ind.cells[args.entityId]?.[args.year];
    if (prev?.editable === false) return false; // readonly satır — FE kapısı
    const rollback = (gr: KpiResolvedGrid): KpiResolvedGrid =>
      patchGridCell(
        gr,
        args.metricId,
        args.entityId,
        args.year,
        () => prev ?? { status: "empty", isDerived: false, editable: true },
      );
    set({
      pendingSaves: get().pendingSaves + 1,
      pendingCells: bumpPending(get().pendingCells, key, 1),
      grid: patchGridCell(grid, args.metricId, args.entityId, args.year, (p) => ({
        ...(p ?? { status: "empty" as const, isDerived: false, editable: true }),
        status: args.status,
        ...(args.numericValue !== undefined ? { value: args.numericValue } : { value: undefined }),
        ...(args.textValue !== undefined ? { text: args.textValue } : { text: undefined }),
      })),
    });
    try {
      const res = await putPortalValueRaw({
        setId,
        metricId: args.metricId,
        entityId: args.entityId,
        year: args.year,
        periodType: "annual",
        status: args.status,
        unit: ind.unit ?? null,
        ...(args.numericValue !== undefined ? { numericValue: args.numericValue } : {}),
        ...(args.textValue !== undefined ? { textValue: args.textValue } : {}),
        ...(args.footnote !== undefined ? { footnote: args.footnote } : {}),
        ...(args.clientNote !== undefined ? { clientNote: args.clientNote } : {}),
        ...(prev?.updatedAt ? { clientUpdatedAt: prev.updatedAt } : {}),
      });
      if (!res.ok) {
        if ("unitChanged" in res) {
          // DR9: sunucu yazmadı — hücre geri, birim yerelde (load yok), amber satır durumu hücrede.
          // I2: komşu hücreler yeni birimde çevrilmiş sayılarla gelsin — yalnız değerler yenilenir
          // (refreshGrid `unitChanged`/hata durumuna dokunmaz)
          const { grid: g, fullGrid: fg } = get();
          const errs = { ...get().saveErrors };
          delete errs[key]; // önceki hata satırı amber satırla çakışmasın
          set({
            saveErrors: errs,
            grid: g ? patchIndicatorUnit(rollback(g), args.metricId, res.current) : null,
            fullGrid: fg ? patchIndicatorUnit(fg, args.metricId, res.current) : null,
            unitChanged: {
              ...get().unitChanged,
              [key]: buildPortalUnitChanged(args, ind.unit ?? null, res.current),
            },
          });
          await get().refreshGrid();
          return false;
        }
        const g = get().grid;
        const rolled = g ? rollback(g) : null;
        if (res.stale) {
          set({
            ...(rolled ? { grid: rolled } : {}),
            stale: { key, current: res.current, message: res.message },
          });
        } else {
          set({
            ...(rolled ? { grid: rolled } : {}),
            saveErrors: { ...get().saveErrors, [key]: portalErrorMessage(res.code, res.message) },
          });
        }
        return false;
      }
      const errs = { ...get().saveErrors };
      delete errs[key];
      const unitChanged = { ...get().unitChanged };
      delete unitChanged[key]; // DR9: yeni birimde yazıldı — amber satır kalkar
      set({ saveErrors: errs, unitChanged, lastSavedAt: res.value.updatedAt });
      const g = get().grid;
      if (g) {
        set({
          grid: patchGridCell(g, args.metricId, args.entityId, args.year, (p) => ({
            ...(p ?? { status: "empty" as const, isDerived: false, editable: true }),
            valueId: res.value.id,
            version: res.value.version,
            updatedAt: res.value.updatedAt,
            updatedBy: res.value.updatedBy,
            updatedByKind: "client",
            workflowState: res.value.workflowState as NonNullable<KpiResolvedCell["workflowState"]>,
          })),
        });
      }
      await get().refreshGrid();
      return true;
    } catch (err) {
      const g = get().grid;
      set({
        saveErrors: {
          ...get().saveErrors,
          [key]: err instanceof Error ? err.message : "Kaydedilemedi",
        },
        ...(g ? { grid: rollback(g) } : {}),
      });
      return false;
    } finally {
      // refreshGrid beklendikten SONRA bırakılır: toplam yeni değer gelene kadar meşgul kalır
      set({
        pendingSaves: Math.max(0, get().pendingSaves - 1),
        pendingCells: bumpPending(get().pendingCells, key, -1),
      });
    }
  },

  async acknowledgeStale() {
    set({ stale: null });
    await get().refreshGrid();
  },

  reset() {
    set({
      userId: null,
      setsStatus: "idle",
      sets: [],
      allSets: [],
      clientFilter: null,
      setId: null,
      entities: [],
      entityId: null,
      entityChoice: false,
      grid: null,
      fullGrid: null,
      fill: null,
      access: null,
      cycle: null,
      error: null,
      pendingCells: {},
      saveErrors: {},
      lastSavedAt: null,
      stale: null,
      unitChanged: {},
    });
  },
}));
